import { logger } from '@/utils/logger/logger';
import type {
  DesktopControlLease,
  DesktopControlOwner,
  DesktopControlOwnerPatch,
} from '@heidi/plugin-rpa';

import {
  DESKTOP_CONTROL_LEASE_REFRESH_INTERVAL_MS,
  refreshDesktopControlLeaseOrThrow,
  releaseDesktopControlLease,
} from './desktop-control-lease';

export { DesktopControlLeaseBusyError } from './desktop-control-lease';

export interface DesktopControlLeaseHolder {
  leaseId: string;
  refreshNow: () => Promise<DesktopControlLease>;
  stop: () => void;
  release: () => Promise<void>;
}

interface HoldDesktopControlLeaseArgs {
  leaseId: string;
  owner?: DesktopControlOwner | null;
  intervalMs?: number;
  onRefreshFailure?: (error: unknown) => void;
}

export const buildDesktopControlOwnerPatch = (
  owner: DesktopControlOwner
): DesktopControlOwnerPatch => ({
  ...(owner.agentRunId && { agentRunId: owner.agentRunId }),
  ...(owner.projectId && { projectId: owner.projectId }),
  ...(owner.projectTitle && { projectTitle: owner.projectTitle }),
  ...(owner.toolCallId && { toolCallId: owner.toolCallId }),
  ...(owner.label && { label: owner.label }),
});

export const holdDesktopControlLease = ({
  leaseId,
  owner,
  intervalMs = DESKTOP_CONTROL_LEASE_REFRESH_INTERVAL_MS,
  onRefreshFailure,
}: HoldDesktopControlLeaseArgs): DesktopControlLeaseHolder => {
  let stopped = false;
  const refreshNow = async (): Promise<DesktopControlLease> =>
    refreshDesktopControlLeaseOrThrow(
      leaseId,
      owner ? buildDesktopControlOwnerPatch(owner) : undefined
    );

  const timer = setInterval(() => {
    void refreshNow().catch((error: unknown) => {
      onRefreshFailure?.(error);
    });
  }, intervalMs);

  const stop = (): void => {
    if (stopped) return;
    stopped = true;
    clearInterval(timer);
  };

  return {
    leaseId,
    refreshNow,
    stop,
    release: async () => {
      stop();
      await releaseDesktopControlLease(leaseId);
    },
  };
};

export const releaseDesktopControlLeaseQuietly = async (
  leaseId: string,
  context: Record<string, unknown> = {}
): Promise<void> => {
  try {
    await releaseDesktopControlLease(leaseId);
  } catch (error) {
    logger.warn('[desktop-control] Failed to release held lease', {
      ...context,
      error: error instanceof Error ? error.message : String(error),
    });
  }
};
